import { Suspense, lazy } from 'react'
import { Navigate, Route, Routes } from 'react-router-dom'
import { AuthProvider, useAuth } from '@/lib/auth'
import { ToastProvider, Spinner } from '@/components/ui'
import { AppShell } from '@/components/layout/AppShell'
import { ForcedPasswordGate } from '@/components/layout/ChangePassword'
import { landingFor } from '@/lib/modules'
import ModulePicker from '@/pages/ModulePicker'

/**
 * THE SERVICE SIDE, AND THE ONLY PLACE THE TWO HALVES MEET.
 *
 * lazy() is what makes Service its own downloaded bundle. Everything reachable
 * from ServiceApp - its shell, its nav, its seven screens - is compiled into a
 * chunk that is fetched the first time somebody opens /service and never at
 * all for the ~45 people who only ever use Materials.
 *
 * This one import is the entire coupling between the two sides.
 * scripts/check-module-boundary.py fails the build if a second one appears.
 */
const ServiceApp = lazy(() => import('@/service/ServiceApp'))

/**
 * PROJECT FINANCE, the third side (01-10-2026). Its own bundle in exactly the
 * same way: one lazy import, fetched only by the people who open /finance.
 * scripts/check-module-boundary.py holds it to the same rules as Service.
 */
const FinanceApp = lazy(() => import('@/finance/FinanceApp'))

import { Login } from '@/pages/auth/Login'
import { Dashboard } from '@/pages/Dashboard'
import { IndentDetail, IndentList, IndentNew } from '@/pages/indent/Indents'
import { PrList } from '@/pages/pr/PrList'
import { PrNew } from '@/pages/pr/PrNew'
import { PrDetail } from '@/pages/pr/PrDetail'
import { IstList, IstNew, IstDetail } from '@/pages/ist/Ist'
import { PoList } from '@/pages/po/PoList'
import { PoBuilder } from '@/pages/po/PoBuilder'
import { PoDetail } from '@/pages/po/PoDetail'
import { GrnList, GrnDetail } from '@/pages/grn/GrnList'
import { GrnNew } from '@/pages/grn/GrnNew'
import { StoreDocList, StoreDocNew, StoreDocDetail } from '@/pages/stores/StoreDocs'
import { Inventory } from '@/pages/inventory/Inventory'
import { Reports } from '@/pages/reports/Reports'
import { Assets, Masters, Payables, SyncQueue, Users } from '@/pages/misc/Misc'
import { PayableDetail } from '@/pages/payables/PayableDetail'
import { Rates } from '@/pages/rates/Rates'
import { InvoiceDetail } from '@/pages/payables/InvoiceDetail'
import { CreditNotes, Payments } from '@/pages/payables/Registers'
import { VendorInvoices } from '@/pages/payables/VendorInvoices'
import GrnRegister from '@/pages/payables/GrnRegister'
import Liabilities from '@/pages/payables/Liabilities'
import PettyCash from '@/pages/payables/PettyCash'
import OpeningLiabilities from '@/pages/payables/OpeningLiabilities'
import { OpeningPaymentDetail } from '@/pages/payables/OpeningPaymentDetail'
import { Periods } from '@/pages/admin/Periods'
import { LateEntries } from '@/pages/admin/LateEntries'
import { SystemSettingsPage } from '@/pages/admin/SystemSettingsPage'
import { PrHousekeeping } from '@/pages/pr/PrHousekeeping'

function Protected({ children }: { children: React.ReactNode }) {
  const { profile, loading } = useAuth()
  if (loading) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3">
        <img src="/logo-oval.png" alt="" className="h-12 w-12 object-contain" />
        <Spinner className="h-5 w-5 text-oval-600" />
        <p className="text-sm text-slate-500">Starting OVAL IMS…</p>
      </div>
    )
  }
  if (!profile) return <Navigate to="/login" replace />
  // 14-10-2026: covers all three modules and the chooser. Draws nothing unless
  // this person signed in with the shared starting password.
  return <>{children}<ForcedPasswordGate /></>
}

/**
 * Where a fresh sign-in lands.
 *
 * Entitled to one side only -> straight there, and the two cards are never
 * seen. Entitled to both -> where you were last, or the chooser the first
 * time. All of that reasoning is in lib/modules.ts; this only obeys it.
 */
function Landing() {
  const { profile } = useAuth()
  return <Navigate to={landingFor(profile?.role)} replace />
}

/** Shown for the moment a module's chunk is coming down the wire. */
function ModuleLoading({ what = 'Service Procurement' }: { what?: string }) {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-3">
      <img src="/logo-oval.png" alt="" className="h-12 w-12 object-contain" />
      <Spinner className="h-5 w-5 text-amber-600" />
      <p className="text-sm text-slate-500">Opening {what}…</p>
    </div>
  )
}

export default function App() {
  return (
    <ToastProvider>
      <AuthProvider>
        <Routes>
          <Route path="/login" element={<Login />} />

          {/* The two cards. Its own full-page route with no shell, because it
              is the moment BEFORE you have chosen a side and neither shell is
              right yet. */}
          <Route path="/choose" element={<Protected><ModulePicker /></Protected>} />

          {/* Everything under /service. One lazy import, one bundle. */}
          <Route path="/service/*" element={
            <Protected>
              <Suspense fallback={<ModuleLoading />}>
                <ServiceApp />
              </Suspense>
            </Protected>
          } />

          {/* Everything under /finance. One lazy import, one bundle. */}
          <Route path="/finance/*" element={
            <Protected>
              <Suspense fallback={<ModuleLoading what="Project Finance" />}>
                <FinanceApp />
              </Suspense>
            </Protected>
          } />

          {/* Where a sign-in lands, before the Materials shell is drawn. */}
          <Route path="/start" element={<Protected><Landing /></Protected>} />

          <Route element={<Protected><AppShell /></Protected>}>
            <Route index element={<Dashboard />} />

            <Route path="indents" element={<IndentList />} />
            <Route path="indents/new" element={<IndentNew />} />
            <Route path="indents/:id" element={<IndentDetail />} />
            <Route path="indents/:id/edit" element={<IndentNew />} />

            <Route path="prs" element={<PrList />} />
            <Route path="prs/new" element={<PrNew />} />
            <Route path="prs/:id" element={<PrDetail />} />
            <Route path="prs/:id/edit" element={<PrNew />} />

            <Route path="ists" element={<IstList />} />
            <Route path="ists/new" element={<IstNew />} />
            <Route path="ists/:id" element={<IstDetail />} />
            <Route path="ists/:id/edit" element={<IstNew />} />

            <Route path="pos" element={<PoList />} />
            <Route path="pos/new" element={<PoBuilder />} />
            <Route path="pos/:id" element={<PoDetail />} />
            <Route path="pos/:id/edit" element={<PoBuilder />} />

            <Route path="grns" element={<GrnList />} />
            <Route path="grns/new" element={<GrnNew />} />
            <Route path="grns/:id" element={<GrnDetail />} />
            <Route path="grns/:id/edit" element={<GrnNew />} />

            <Route path="mins" element={<StoreDocList mode="MIN" />} />
            <Route path="mins/new" element={<StoreDocNew mode="MIN" />} />
            <Route path="mins/:id" element={<StoreDocDetail mode="MIN" />} />
            <Route path="mins/:id/edit" element={<StoreDocNew mode="MIN" />} />

            <Route path="mrns" element={<StoreDocList mode="MRN" />} />
            <Route path="mrns/new" element={<StoreDocNew mode="MRN" />} />
            <Route path="mrns/:id" element={<StoreDocDetail mode="MRN" />} />
            <Route path="mrns/:id/edit" element={<StoreDocNew mode="MRN" />} />

            <Route path="inventory" element={<Inventory />} />
            <Route path="assets" element={<Assets />} />
            <Route path="payables" element={<Payables />} />
            <Route path="payables/:id" element={<PayableDetail />} />
            <Route path="vendor-invoices" element={<VendorInvoices />} />
            <Route path="grn-register" element={<GrnRegister />} />
            <Route path="liabilities" element={<Liabilities />} />
            <Route path="petty-cash" element={<PettyCash />} />
            <Route path="opening-liabilities" element={<OpeningLiabilities />} />
            <Route path="opening-liabilities/payments/:id" element={<OpeningPaymentDetail />} />
            <Route path="periods" element={<Periods />} />
            <Route path="system-settings" element={<SystemSettingsPage />} />
            <Route path="pr-housekeeping" element={<PrHousekeeping />} />
            <Route path="invoices/:id" element={<InvoiceDetail />} />
            <Route path="credit-notes" element={<CreditNotes />} />
            <Route path="payments" element={<Payments />} />
            <Route path="rates" element={<Rates />} />
            <Route path="reports" element={<Reports />} />
            <Route path="late-entries" element={<LateEntries />} />
            <Route path="masters" element={<Masters />} />
            <Route path="users" element={<Users />} />
            <Route path="sync" element={<SyncQueue />} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AuthProvider>
    </ToastProvider>
  )
}
